import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PageEditorContext = createContext(null);

export const usePageEditor = () => {
  const context = useContext(PageEditorContext);
  if (!context) {
    return { isEditMode: false, isAdmin: false };
  }
  return context;
};

const isGlobalSection = (sectionId) => sectionId.startsWith('global');

export const PageEditorProvider = ({ children }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [pageContent, setPageContent] = useState({});
  const [globalContent, setGlobalContent] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [message, setMessage] = useState(null);

  // Check if user is admin on mount
  useEffect(() => {
    checkAdminStatus();
    loadGlobalContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Auto-enable edit mode when arriving via ?edit=1 (from admin Site Editor)
  useEffect(() => {
    if (isAdmin && new URLSearchParams(window.location.search).get('edit') === '1') {
      setIsEditMode(true);
    }
  }, [isAdmin]);

  const checkAdminStatus = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setIsAdmin(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const perms = data.user?.permissions || [];
        setUserRole(data.user?.role);
        setPermissions(perms);
        // Only users with the 'pages' permission can edit the site inline
        setIsAdmin(perms.includes('pages'));
      } else {
        setIsAdmin(false);
        localStorage.removeItem('adminToken');
      }
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const mergeContentResponse = (data) => {
    const content = {};
    Object.entries(data.content || {}).forEach(([sectionId, sectionData]) => {
      if (sectionData.draft) {
        content[sectionId] = sectionData.draft;
      } else if (sectionData.published) {
        content[sectionId] = sectionData.published;
      }
    });
    return content;
  };

  const loadGlobalContent = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    try {
      if (token) {
        const response = await fetch(`${API_URL}/api/cms/content/global`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setGlobalContent(mergeContentResponse(data));
          return;
        }
      }
      const response = await fetch(`${API_URL}/api/cms/published/global`);
      if (response.ok) {
        const data = await response.json();
        setGlobalContent(data.sections || {});
      }
    } catch (error) {
      // Non-blocking
    }
  }, []);

  const loadPageContent = useCallback(async (pageId) => {
    const token = localStorage.getItem('adminToken');

    try {
      if (token) {
        const response = await fetch(`${API_URL}/api/cms/content/${pageId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPageContent(mergeContentResponse(data));
          setCurrentPage(pageId);
          return;
        }
      }
      // Public visitors - load published content only
      const response = await fetch(`${API_URL}/api/cms/published/${pageId}`);
      if (response.ok) {
        const data = await response.json();
        setPageContent(data.sections || {});
        setCurrentPage(pageId);
      }
    } catch (error) {
      console.error('Error loading page content:', error);
    }
  }, []);

  const getContent = useCallback((sectionId, field, defaultValue = '') => {
    // First check pending changes
    if (pendingChanges[sectionId]?.[field] !== undefined) {
      return pendingChanges[sectionId][field];
    }
    // Then check loaded CMS content
    if (pageContent[sectionId]?.[field] !== undefined) {
      return pageContent[sectionId][field];
    }
    // Then site-wide (header/footer) content
    if (globalContent[sectionId]?.[field] !== undefined) {
      return globalContent[sectionId][field];
    }
    // Return default
    return defaultValue;
  }, [pendingChanges, pageContent, globalContent]);

  const updateContent = useCallback((sectionId, field, value) => {
    setPendingChanges(prev => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  }, []);

  const postSection = async (token, pageId, sectionId, content) => {
    await fetch(`${API_URL}/api/cms/content`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        page_id: pageId,
        section_id: sectionId,
        content,
        status: 'draft'
      })
    });
  };

  const savePendingAsDrafts = async (token) => {
    for (const [sectionId, content] of Object.entries(pendingChanges)) {
      const targetPage = isGlobalSection(sectionId) ? 'global' : currentPage;
      if (!targetPage) continue;
      const existingContent = (isGlobalSection(sectionId) ? globalContent[sectionId] : pageContent[sectionId]) || {};
      const mergedContent = { ...existingContent, ...content };
      await postSection(token, targetPage, sectionId, mergedContent);
    }
  };

  const saveAsDraft = async () => {
    if (Object.keys(pendingChanges).length === 0) return;

    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setIsSaving(true);
    try {
      await savePendingAsDrafts(token);
      setMessage({ type: 'success', text: 'Draft saved! Changes are not live yet.' });
      setHasUnsavedChanges(false);
      if (currentPage) await loadPageContent(currentPage);
      await loadGlobalContent();
      setPendingChanges({});
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save draft' });
    } finally {
      setIsSaving(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const publishDraftsForPage = async (token, pageId) => {
    const response = await fetch(`${API_URL}/api/cms/content/${pageId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      for (const [sectionId, sectionData] of Object.entries(data.content || {})) {
        if (sectionData.draft) {
          await fetch(`${API_URL}/api/cms/publish/${pageId}/${sectionId}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
      }
    }
  };

  const publishChanges = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setIsSaving(true);
    try {
      // First save any pending changes as drafts
      if (Object.keys(pendingChanges).length > 0) {
        await savePendingAsDrafts(token);
      }

      // Publish drafts on the current page and site-wide sections
      if (currentPage) await publishDraftsForPage(token, currentPage);
      await publishDraftsForPage(token, 'global');

      setMessage({ type: 'success', text: 'Changes published! Now live on the website.' });
      setHasUnsavedChanges(false);
      setPendingChanges({});

      if (currentPage) await loadPageContent(currentPage);
      await loadGlobalContent();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to publish changes' });
    } finally {
      setIsSaving(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const discardChanges = () => {
    setPendingChanges({});
    setHasUnsavedChanges(false);
    setIsEditMode(false);
  };

  const toggleEditMode = () => {
    if (isEditMode && hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
      discardChanges();
    }
    setIsEditMode(!isEditMode);
  };

  return (
    <PageEditorContext.Provider value={{
      isEditMode,
      setIsEditMode,
      isAdmin,
      userRole,
      permissions,
      currentPage,
      setCurrentPage,
      loadPageContent,
      getContent,
      updateContent,
      pendingChanges,
      hasUnsavedChanges,
      isSaving,
      saveAsDraft,
      publishChanges,
      discardChanges,
      toggleEditMode,
      message,
      checkAdminStatus
    }}>
      {children}
    </PageEditorContext.Provider>
  );
};

export default PageEditorContext;
